'use client';
import { CustomerSessionSync } from '@eliadmin/core/src/components/providers/customer-session-sync';
import { trackPageView } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { TextOverflowFade } from '@eliadmin/core/src/components/root/text-overflow-fade';
import { CookieConsentFab } from '@eliadmin/core/src/components/cookie-consent/cookie-consent-fab';
import { GeneratedTranslationBoundary } from '@eliadmin/core/src/components/providers/generated-translation-boundary';
import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { Toaster } from '@eliadmin/core/src/components/ui/toaster';
import { useTranslationSnapshot } from '@eliadmin/core/src/i18n/translation-context';
import { getMe } from '@eliadmin/core/src/server/api-client';
import { CategoryProvider } from '@eliadmin/core/src/stores/category-store';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { initGlobalStore, stopGlobalNavigation } from '@eliadmin/core/src/stores/global-store';
import type { LocaleConfig } from '@eliadmin/core/src/stores/locale-store';
import {
  initLocaleStore,
  setCurrentLocale,
  useLocaleStore,
} from '@eliadmin/core/src/stores/locale-store';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryStreamedHydration } from '@tanstack/react-query-next-experimental';
import { AppProgressBar } from 'next-nprogress-bar';
import { usePathname, useSearchParams } from 'next/navigation';
import type { PropsWithChildren } from 'react';
import React, { Suspense, useEffect, useState } from 'react';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { CurriculumReminderController } from '../jobportal/curriculum-reminder-controller';
import { ProfileNavigationObserver } from '../profile/profile-navigation';

// Custom Loading Animation Component
const LoadingAnimation = () => {
  return (
    <div className="flex flex-col items-center justify-center gap-6">
      {/* Logo with pulse animation */}
      <div className="relative">
        <div className="absolute inset-0 h-[200px] w-[200px] animate-ping rounded-full bg-primary/20 duration-1000 sm:h-[400px] sm:w-[400px]"></div>
        <div className="relative">
          <Image
            src="/images/jobinterview.avif"
            alt="JobInterview Logo"
            width={200}
            height={200}
            className="h-[133px] w-[200px] object-contain sm:h-[300px] sm:w-[400px]"
            priority
          />
        </div>
      </div>

      {/* Animated dots */}
      <div className="flex space-x-2">
        <div
          className="h-3 w-3 animate-bounce rounded-full bg-primary [animation-delay:-0.4s]"
          style={{ animationDuration: '0.6s', transform: 'translateY(-12px)' }}
        ></div>
        <div
          className="h-3 w-3 animate-bounce rounded-full bg-primary [animation-delay:-0.3s]"
          style={{ animationDuration: '0.6s', transform: 'translateY(-12px)' }}
        ></div>
        <div
          className="h-3 w-3 animate-bounce rounded-full bg-primary [animation-delay:-0.2s]"
          style={{ animationDuration: '0.6s', transform: 'translateY(-12px)' }}
        ></div>
        <div
          className="h-3 w-3 animate-bounce rounded-full bg-primary [animation-delay:-0.1s]"
          style={{ animationDuration: '0.6s', transform: 'translateY(-12px)' }}
        ></div>
        <div
          className="h-3 w-3 animate-bounce rounded-full bg-primary"
          style={{ animationDuration: '0.6s', transform: 'translateY(-12px)' }}
        ></div>
      </div>
    </div>
  );
};

export type AppProviderProps = {
  localeConfig: LocaleConfig;
  globalState: Parameters<typeof initGlobalStore>[0];
};

export function AppProvider(props: PropsWithChildren<AppProviderProps>) {
  return (
    <GeneratedTranslationBoundary locale={props.localeConfig.currentLocale}>
      <AppProviderClient {...props} />
    </GeneratedTranslationBoundary>
  );
}

function AppProviderClient(props: PropsWithChildren<AppProviderProps>) {
  const translationSnapshot = useTranslationSnapshot();
  const globalState = React.useMemo(
    () => ({
      ...props.globalState,
      translations: translationSnapshot ?? props.globalState.translations,
    }),
    [props.globalState, translationSnapshot],
  );
  const [queryClient] = React.useState(() => new QueryClient());
  const isInitializedRef = React.useRef(false);
  // Already translated server HTML must not wait behind another priority image
  // and a hydration-dependent splash. Keep the legacy missing-dictionary fallback.
  const [showLoading, setShowLoading] = useState(
    () => translationSnapshot === null && Object.keys(globalState.translations ?? {}).length === 0,
  );
  const { isReady } = useTranslations();

  // this is to make sure store is set for first render cycle
  if (!isInitializedRef.current) {
    initLocaleStore(props.localeConfig);
    initGlobalStore(globalState);
    // initCustomerStore(null);
    isInitializedRef.current = true;
  }

  // sync store with server state
  useEffect(() => {
    initLocaleStore(props.localeConfig);
    initGlobalStore(globalState);
  }, [globalState, props.localeConfig]);

  const oldLocale = useLocaleStore(s => s.currentLocale);

  useEffect(() => {
    if (props.localeConfig.currentLocale !== oldLocale) {
      setCurrentLocale(props.localeConfig.currentLocale);
    }
  }, [oldLocale, props.localeConfig.currentLocale]);

  // Show loading overlay for 3 seconds then fade away
  //   useEffect(() => {
  //     const timer = setTimeout(() => {
  //       setShowLoading(false);
  //     }, 1000);

  //     return () => clearTimeout(timer);
  //   }, []);

  useEffect(() => {
    if (isReady) {
      setShowLoading(false);
    }
  }, [isReady]);

  return (
    <QueryClientProvider client={queryClient}>
      <CustomerSessionSync />
      <TextOverflowFade />
      <ReactQueryStreamedHydration>
        <Suspense fallback={null}>
          <AppProgressBar
            height="4px"
            color={props.globalState.generalLayout.color}
            options={{
              showSpinner: false,
            }}
            shallowRouting
          />
          {showLoading && (
            <div
              role="status"
              aria-label="Načítavanie"
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-white"
            >
              <LoadingAnimation />
            </div>
          )}
          <NavigationEvents />
          <ProfileNavigationObserver />
        </Suspense>
        <Toaster />
        <CategoryProvider>
          <DialogProvider>
            {props.children}
            <CurriculumReminderController />
            <CookieConsentFab />
          </DialogProvider>
        </CategoryProvider>
      </ReactQueryStreamedHydration>
    </QueryClientProvider>
  );
}

function NavigationEvents() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const url = `${pathname}?${searchParams.toString()}`;

  useEffect(() => {
    stopGlobalNavigation();
  }, [url]);

  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);

  return null;
}
